export function matchesSearch(query: string, text: string) {
  const haystack = text.normalize("NFKC").toLocaleLowerCase();
  return query.normalize("NFKC").toLocaleLowerCase().trim().split(/\s+/).filter(
    Boolean,
  ).every((term) => haystack.includes(term.replace(/^#/, "")));
}
export type PrIdentity = { number: number; repo?: string };
const aliases: Record<string, string> = {
  admin: "otomator-admin",
  jumpstart: "jumpstartapp",
};
const repoName = (name: string) =>
  (aliases[name] ?? name).replace(/^the-otomator\//, "");
export function parsePrSearch(query: string): PrIdentity | null {
  const text = query.normalize("NFKC").trim().toLowerCase();
  let number: string | undefined, repo: string | undefined;
  const bare = /^#?(\d+)$/.exec(text);
  const qualified = /^(?:the-otomator\/)?([\w.-]+)(?:\s*#\s*|\s+#?)(\d+)$/.exec(
    text,
  );
  if (bare) number = bare[1];
  else if (qualified) {
    repo = repoName(qualified[1]);
    number = qualified[2];
  } else {
    try {
      const url = new URL(text),
        match = /^\/the-otomator\/([\w.-]+)\/pull\/(\d+)\/?$/.exec(
          url.pathname,
        );
      if (
        url.protocol !== "https:" || url.hostname !== "github.com" || !match
      ) return null;
      repo = repoName(match[1]);
      number = match[2];
    } catch {
      return null;
    }
  }
  const value = Number(number);
  return Number.isSafeInteger(value) && value > 0
    ? { number: value, ...(repo ? { repo } : {}) }
    : null;
}
export type SearchPr = {
  number: number;
  repo: string;
  title: string;
  url?: string;
  pr_body?: string;
  task?: { id?: string; title?: string; context?: string | null } | null;
  control_snapshot?: unknown;
  migrations?: unknown;
};
export function prSearchRank(query: string, pr: SearchPr) {
  const identity = parsePrSearch(query);
  if (
    identity && pr.number === identity.number &&
    (!identity.repo || repoName(pr.repo.toLowerCase()) === identity.repo)
  ) return 100;
  if (identity?.repo) return 0;
  const text = `${pr.number} ${pr.title} ${pr.repo} ${pr.url ?? ""} ${
    pr.pr_body ?? ""
  } ${pr.task?.title ?? ""} ${pr.task?.context ?? ""} ${
    JSON.stringify(pr.control_snapshot ?? {})
  } ${JSON.stringify(pr.migrations ?? [])}`;
  return matchesSearch(query, text) ? 1 : 0;
}
export function rankPrResults<T extends SearchPr>(
  query: string,
  prs: T[],
  matchingTasks: string[] = [],
) {
  return prs.map((pr) => ({
    pr,
    rank: Math.max(
      prSearchRank(query, pr),
      pr.task?.id && matchingTasks.includes(pr.task.id) ? 1 : 0,
    ),
  })).filter((r) => r.rank > 0).sort((a, b) =>
    b.rank - a.rank || a.pr.repo.localeCompare(b.pr.repo) ||
    a.pr.number - b.pr.number
  ).map((r) => r.pr);
}
